import { FC, useMemo, useState } from "react";
import {
  AddressId,
  Nullable,
  useAdminNotificationControllerDelete,
  useAdminNotificationControllerSend,
  useAdminNotificationControllerUpdate,
} from "service";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import { useNavigate } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { StatusDot, generateRoute } from "ui";
import { useDialogStore } from "../../../state/dialog-store";
import { toast } from "react-toastify";
import { AdminNotificationResDto } from "service/api/estabrookSchemas";
import InteractableCell from "../components/generic-table/interactable-cell";
import {
  NotificationsDeliveryTabEnum,
  NotificationsStatusEnum,
  notificationsRecurrenceEnumLabelMap,
  notificationsStatusEnumLabelMap,
} from "service/types/enum/notifications-enum";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import { capitalize, trimEnd } from "lodash";
import {
  formatDateTime,
  formattedRecurrence,
} from "../../../utils/string-helpers";
import TransitionRoutesPhaseModal from "../route-planner/transition-routes-modal";

const sanitizedHeader = [
  {
    label: "Title",
    value: "title",
    size: "16%",
  },
  {
    label: "Type",
    value: "type",
    size: "10%",
  },
  {
    label: "Recipients",
    value: "recipients",
    size: "8%",
  },
  {
    label: "Date Modified",
    value: "dateModified",
    size: "14%",
    sortable: true,
    sortableValue: "updatedAt",
  },
  {
    label: "Status",
    value: "status",
    size: "14%",
  },
];

export interface NotificationsManagementTableProps {
  data?: AdminNotificationResDto[];
  sortOptions?: Nullable<SortOptions>;
  sortOptionsChange?: (value: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  selectedTab?: NotificationsDeliveryTabEnum;
}

const NotificationsManagementTable: FC<NotificationsManagementTableProps> = (
  props,
) => {
  const {
    data = [],
    sortOptions,
    sortOptionsChange,
    isLoading,
    selectedTab,
  } = props;
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const { palette } = useTheme();
  const [notificationSentRecipients, setNotificationSentRecipients] = useState<
    number | undefined
  >(undefined);

  const { mutate } = useAdminNotificationControllerDelete({
    onSuccess: () => {
      toast.success("Deleted successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/notifications",
          operationId: "adminNotificationControllerGetAll",
          variables: {},
        }),
      );
    },
  });

  const { mutate: editNotification } = useAdminNotificationControllerUpdate({
    onSuccess: () => {
      toast.success("Notification Updated Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/notifications",
          operationId: "adminNotificationControllerGetAll",
          variables: {},
        }),
      );
    },
  });

  const { mutate: sendNotification } = useAdminNotificationControllerSend({
    onSuccess: (data) => {
      setNotificationSentRecipients(data.data?.recipientsCount);
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/notifications",
          operationId: "adminNotificationControllerGetAll",
          variables: {},
        }),
      );
    },
  });

  const onNotificationDetail = (id: AddressId) => {
    navigate(
      generateRoute(AdRoutes.notificationDetails, { notificationId: id }),
    );
  };

  const onDeletePress = async (notificationId: string) => {
    setDialog({
      title: "Delete notification",
      description: `Are you sure you want to delete this notification? <br />This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        mutate({ pathParams: { notificationId } });
        onClose();
      },
    });
  };
  const onSendPress = async (notificationId: string, title: string) => {
    setDialog({
      title: "Send notification now",
      description: `Are you sure you want to send the notification "${title}" now?`,
      confirmText: "Send now",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        sendNotification({ pathParams: { notificationId } });
        onClose();
      },
    });
  };

  const onPausePress = async (notificationId: string, paused: boolean) => {
    setDialog({
      title: `${paused ? "Unpause" : "Pause"} Notification`,
      description: `Are you sure you want to ${
        paused ? "unpause" : "pause"
      } this notification?`,
      confirmText: paused ? "Unpause" : "Pause",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        editNotification({
          pathParams: { notificationId },
          body: { paused: !paused },
        });
        onClose();
      },
    });
  };

  const headers = useMemo(() => {
    const headers = [...sanitizedHeader];

    if (selectedTab === NotificationsDeliveryTabEnum.automated) {
      // insert trigger column
      headers.splice(2, 0, {
        label: "Trigger",
        value: "trigger",
        size: "20%",
      });
    } else {
      // insert last sent column
      headers.splice(4, 0, {
        label: "Last Sent",
        value: "lastSent",
        size: "14%",
      });
      // insert scheduled column
      headers.splice(5, 0, {
        label: "Scheduled",
        value: "scheduled",
        size: "14%",
      });
    }
    if (selectedTab === NotificationsDeliveryTabEnum.oneTime) {
      // insert delivery column
      headers.splice(2, 0, {
        label: "Delivery",
        value: "delivery",
        size: "10%",
      });
    }
    return headers;
  }, [selectedTab]);

  const renderRowLabelMap: Record<
    string,
    RenderGenericRowFunction<AdminNotificationResDto>
  > = useMemo(
    () => ({
      title: ({ headerValue, data: { title } }) => ({
        label: title,
        value: headerValue,
      }),
      type: ({ headerValue, data: { type } }) => ({
        label: (
          <Box sx={{ display: "flex", gap: "6px" }}>
            {type.map((t, index) => (
              <Chip
                key={index}
                label={capitalize(t)}
                sx={{
                  backgroundColor:
                    t === "push" ? palette.grey[400] : palette.grey[500],
                }}
                data-testid="notification-type"
              />
            ))}
          </Box>
        ),
        value: headerValue,
      }),
      delivery: ({ headerValue, data: { deliveryType } }) => ({
        label: capitalize(deliveryType),
        value: headerValue,
      }),
      recipients: ({ headerValue, data: { recipientCount } }) => ({
        label: recipientCount,
        value: headerValue,
      }),
      dateModified: ({ headerValue, data: { updatedAt } }) => ({
        label: formatDateTime(updatedAt),
        value: headerValue,
      }),
      lastSent: ({ headerValue, data: { lastSent } }) => ({
        label: lastSent && formatDateTime(String(lastSent)),
        value: headerValue,
      }),
      scheduled: ({ headerValue, data }) => {
        if (data.recurrence) {
          return {
            label: (
              <Box>
                <Typography>
                  {trimEnd(
                    notificationsRecurrenceEnumLabelMap[data.recurrence],
                    "...",
                  )}
                </Typography>
                {data.customInterval && (
                  <Typography variant="body2" color="neutral">
                    {formattedRecurrence(data)}
                  </Typography>
                )}
              </Box>
            ),
            value: headerValue,
          };
        }
        return {
          label: data.date && formatDateTime(data.date),
          value: headerValue,
        };
      },
      trigger: ({ headerValue, data: { description } }) => ({
        label: description,
        value: headerValue,
      }),
      status: ({ headerValue, data }) => {
        const { id, title, lastSent, recipientCount, paused, deliveryType } =
          data;

        const status = lastSent
          ? NotificationsStatusEnum.sent
          : recipientCount
            ? NotificationsStatusEnum.notSent
            : NotificationsStatusEnum.draft;
        return {
          label: (
            <InteractableCell
              label={
                deliveryType === "recurring" ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <StatusDot
                      status={
                        paused
                          ? NotificationsStatusEnum.notSent
                          : NotificationsStatusEnum.sent
                      }
                    />
                    {paused ? "Paused" : "Enabled"}
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <StatusDot
                      status={
                        lastSent
                          ? NotificationsStatusEnum.sent
                          : NotificationsStatusEnum.draft
                      }
                    />
                    {notificationsStatusEnumLabelMap[status]}
                  </Box>
                )
              }
              onEdit={() => onNotificationDetail(id)}
              onDelete={() => onDeletePress(id)}
              {...(deliveryType === "recurring"
                ? {
                    onPause: () => onPausePress(id, paused),
                    isPaused: paused,
                  }
                : {
                    onSend: () => onSendPress(id, title),
                    onSendDisabled: recipientCount === 0,
                  })}
            />
          ),
          value: headerValue,
        };
      },
    }),
    [],
  );

  return (
    <>
      <GenericTable
        sx={{ mt: "20px" }}
        headers={headers}
        renderRowLabel={renderRowLabelMap}
        data={data}
        sortOptions={sortOptions}
        onSortChange={sortOptionsChange}
        isLoading={isLoading}
      />
      <TransitionRoutesPhaseModal
        open={notificationSentRecipients !== undefined}
        isCustom
        title={"Notification sent"}
        description={`Notification sent to ${notificationSentRecipients} recipients.`}
        onClose={() => {
          setNotificationSentRecipients(undefined);
        }}
      />
    </>
  );
};

export default NotificationsManagementTable;
